import type { Node, Edge } from '@xyflow/react';

/* ------------------------------------------------------------------ */
/*  Search Demo Data                                                   */
/* ------------------------------------------------------------------ */

/** Phase 1: simple search */
export const DEMO_QUERY_1 = 'level=error source=api-gateway';

/** Phase 2: the pipe command appended while results stay visible */
export const DEMO_QUERY_2_APPEND = ' | timechart span=1h count by level';

/** Full phase 2 query */
export const DEMO_QUERY_2 = 'level=error source=api-gateway | timechart span=1h count by level';

export interface DemoLogEntry {
  timestamp: string;
  level: 'error' | 'warn' | 'info';
  source: string;
  event: string;
}

export const DEMO_LOGS: DemoLogEntry[] = [
  { timestamp: '04/03 14:23:01.412', level: 'error', source: 'api-gateway', event: 'Connection timeout to upstream service payments-svc after 30s' },
  { timestamp: '04/03 14:22:58.891', level: 'error', source: 'api-gateway', event: 'Rate limit exceeded for client_id=acme-corp (429 Too Many Requests)' },
  { timestamp: '04/03 14:22:55.203', level: 'error', source: 'api-gateway', event: 'TLS handshake failed: certificate expired for downstream host orders-svc.internal' },
  { timestamp: '04/03 14:22:51.667', level: 'warn', source: 'api-gateway', event: 'Retry #3 for request POST /api/v2/checkout — upstream returned 503' },
  { timestamp: '04/03 14:22:48.102', level: 'error', source: 'api-gateway', event: 'Circuit breaker OPEN for service inventory-svc — failure rate 78%' },
  { timestamp: '04/03 14:22:44.559', level: 'error', source: 'api-gateway', event: 'Request body too large: 12.4 MB exceeds max_body_size=10MB' },
  { timestamp: '04/03 14:22:40.334', level: 'warn', source: 'api-gateway', event: 'Slow upstream response: auth-svc responded in 4,210ms (threshold: 2,000ms)' },
  { timestamp: '04/03 14:22:36.891', level: 'error', source: 'api-gateway', event: 'DNS resolution failed for service mesh endpoint consul.service.internal' },
];

export interface DemoFieldValue {
  name: string;
  pct: number;
}

export interface DemoField {
  name: string;
  category: 'core' | 'parsed' | 'eval' | 'attribute';
  values: DemoFieldValue[];
}

export const DEMO_FIELDS: DemoField[] = [
  {
    name: 'level',
    category: 'core',
    values: [
      { name: 'error', pct: 62 },
      { name: 'warn', pct: 38 },
    ],
  },
  {
    name: 'source',
    category: 'core',
    values: [
      { name: 'api-gateway', pct: 100 },
    ],
  },
  {
    name: 'host',
    category: 'core',
    values: [
      { name: 'gw-prod-01', pct: 45 },
      { name: 'gw-prod-02', pct: 35 },
      { name: 'gw-prod-03', pct: 20 },
    ],
  },
  {
    name: 'status_code',
    category: 'parsed',
    values: [
      { name: '500', pct: 40 },
      { name: '429', pct: 25 },
      { name: '503', pct: 20 },
      { name: '408', pct: 15 },
    ],
  },
  {
    name: 'service',
    category: 'parsed',
    values: [
      { name: 'payments-svc', pct: 30 },
      { name: 'orders-svc', pct: 25 },
      { name: 'auth-svc', pct: 25 },
      { name: 'inventory-svc', pct: 20 },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Timechart mock SVG data (24 hourly buckets, two series)            */
/* ------------------------------------------------------------------ */

export interface TimechartPoint {
  hour: number;
  error: number;
  warn: number;
}

export const TIMECHART_DATA: TimechartPoint[] = [
  { hour: 0, error: 12, warn: 5 },
  { hour: 1, error: 8, warn: 3 },
  { hour: 2, error: 5, warn: 2 },
  { hour: 3, error: 3, warn: 1 },
  { hour: 4, error: 4, warn: 2 },
  { hour: 5, error: 6, warn: 3 },
  { hour: 6, error: 15, warn: 8 },
  { hour: 7, error: 28, warn: 12 },
  { hour: 8, error: 45, warn: 18 },
  { hour: 9, error: 52, warn: 22 },
  { hour: 10, error: 38, warn: 15 },
  { hour: 11, error: 42, warn: 20 },
  { hour: 12, error: 35, warn: 14 },
  { hour: 13, error: 48, warn: 19 },
  { hour: 14, error: 65, warn: 25 },
  { hour: 15, error: 58, warn: 21 },
  { hour: 16, error: 44, warn: 17 },
  { hour: 17, error: 50, warn: 22 },
  { hour: 18, error: 38, warn: 15 },
  { hour: 19, error: 25, warn: 10 },
  { hour: 20, error: 18, warn: 8 },
  { hour: 21, error: 14, warn: 6 },
  { hour: 22, error: 10, warn: 4 },
  { hour: 23, error: 8, warn: 3 },
];

/** Mock timechart table rows (subset shown) */
export interface TimechartRow {
  time: string;
  error: number;
  warn: number;
}

export const TIMECHART_TABLE: TimechartRow[] = [
  { time: '04/02 14:00', error: 65, warn: 25 },
  { time: '04/02 15:00', error: 58, warn: 21 },
  { time: '04/02 16:00', error: 44, warn: 17 },
  { time: '04/02 17:00', error: 50, warn: 22 },
  { time: '04/02 18:00', error: 38, warn: 15 },
  { time: '04/02 19:00', error: 25, warn: 10 },
];

/* ------------------------------------------------------------------ */
/*  Pipeline Demo Data                                                 */
/* ------------------------------------------------------------------ */

export const PIPELINE_NODES: Node[] = [
  { id: 'start', type: 'pipelineNode', position: { x: 0, y: 130 }, data: { nodeType: 'start', label: 'Webhook Trigger' } },
  { id: 'http', type: 'pipelineNode', position: { x: 210, y: 130 }, data: { nodeType: 'httpRequest', label: 'Fetch Metadata' } },
  { id: 'json', type: 'pipelineNode', position: { x: 420, y: 130 }, data: { nodeType: 'json', label: 'Parse JSON' } },
  { id: 'redact', type: 'pipelineNode', position: { x: 630, y: 130 }, data: { nodeType: 'redactMask', label: 'Redact PII' } },
  { id: 'condition', type: 'pipelineNode', position: { x: 840, y: 105 }, data: { nodeType: 'condition', label: 'Check Level' } },
  { id: 'lookup', type: 'pipelineNode', position: { x: 1050, y: 50 }, data: { nodeType: 'lookup', label: 'Enrich from CMDB' } },
  { id: 'transform', type: 'pipelineNode', position: { x: 1050, y: 220 }, data: { nodeType: 'transform', label: 'Normalize Fields' } },
  { id: 'ingest1', type: 'pipelineNode', position: { x: 1240, y: 50 }, data: { nodeType: 'logpulseIngest', label: 'Ingest \u2192 Alerts' } },
  { id: 'ingest2', type: 'pipelineNode', position: { x: 1240, y: 220 }, data: { nodeType: 'logpulseIngest', label: 'Ingest \u2192 Logs' } },
];

export const PIPELINE_EDGES: Edge[] = [
  { id: 'e1', source: 'start', target: 'http', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e2', source: 'http', target: 'json', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e3', source: 'json', target: 'redact', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e4', source: 'redact', target: 'condition', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e5', source: 'condition', sourceHandle: 'true', target: 'lookup', type: 'bezier', style: { stroke: '#22c55e', strokeWidth: 2 } },
  { id: 'e6', source: 'condition', sourceHandle: 'else', target: 'transform', type: 'bezier', style: { stroke: '#71717a', strokeWidth: 2 } },
  { id: 'e7', source: 'lookup', target: 'ingest1', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
  { id: 'e8', source: 'transform', target: 'ingest2', type: 'bezier', style: { stroke: '#344054', strokeWidth: 2 } },
];

export const PIPELINE_NODE_ORDER = [
  'start', 'http', 'json', 'redact', 'condition', 'lookup', 'transform', 'ingest1', 'ingest2',
];

/* ------------------------------------------------------------------ */
/*  Palette categories — matches apps/web PipelineNodePalette exactly  */
/* ------------------------------------------------------------------ */

export interface PaletteItem {
  label: string;
  description: string;
  iconColor: string;
  /** Override icon key when different from category default */
  iconKey?: string;
}

export interface PaletteCategory {
  key: string;
  label: string;
  color: string;
  items: PaletteItem[];
}

export const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    key: 'trigger',
    label: 'TRIGGER',
    color: 'emerald',
    items: [
      { label: 'Start', description: 'Manual, schedule, webhook', iconColor: 'text-emerald-400' },
    ],
  },
  {
    key: 'extract',
    label: 'EXTRACT',
    color: 'blue',
    items: [
      { label: 'HTTP Request', description: 'Call external APIs', iconColor: 'text-blue-400' },
      { label: 'Splunk Search', description: 'Execute SPL queries', iconColor: 'text-orange-400', iconKey: 'search' },
    ],
  },
  {
    key: 'transform',
    label: 'TRANSFORM',
    color: 'violet',
    items: [
      { label: 'Transform', description: 'Map & JS transform', iconColor: 'text-violet-400', iconKey: 'arrowRightLeft' },
      { label: 'Field Operations', description: 'Add, rename, remove', iconColor: 'text-violet-400', iconKey: 'arrowRightLeft' },
      { label: 'JSON', description: 'Parse or format', iconColor: 'text-blue-400', iconKey: 'fileJson' },
      { label: 'CSV', description: 'Parse or format', iconColor: 'text-teal-400', iconKey: 'fileSpreadsheet' },
      { label: 'Redact / Mask', description: 'PII masking', iconColor: 'text-rose-400', iconKey: 'shield' },
      { label: 'Map Schema', description: 'Data model mapping', iconColor: 'text-indigo-400', iconKey: 'map' },
      { label: 'Lookup', description: 'Enrich from datasets', iconColor: 'text-cyan-400', iconKey: 'database' },
    ],
  },
  {
    key: 'flowControl',
    label: 'FLOW CONTROL',
    color: 'amber',
    items: [
      { label: 'Condition', description: 'If/else branching', iconColor: 'text-amber-400', iconKey: 'gitBranch' },
      { label: 'Loop', description: 'Iterate over arrays', iconColor: 'text-amber-400', iconKey: 'repeat' },
    ],
  },
  {
    key: 'load',
    label: 'LOAD',
    color: 'cyan',
    items: [
      { label: 'LogPulse Ingest', description: 'Send to index', iconColor: 'text-cyan-400' },
    ],
  },
  {
    key: 'utility',
    label: 'UTILITY',
    color: 'slate',
    items: [
      { label: 'Log', description: 'Debug logging', iconColor: 'text-slate-400', iconKey: 'messageSquare' },
      { label: 'Throw Error', description: 'Stop with error', iconColor: 'text-red-400', iconKey: 'alertTriangle' },
    ],
  },
];
